/**
 * WhatsApp deep-link plumbing. PURE, and deliberately framework-free.
 *
 * Nothing in this file imports React, the API client, or anything else — it is
 * string arithmetic over a phone number, and it is the one part of the "Send via
 * WhatsApp" feature that is worth exhaustive unit tests. Keeping it free of
 * imports means those tests need no render harness, no fetch mock and no DOM.
 *
 * SCOPE: this builds a `wa.me` DEEP LINK. It opens WhatsApp Web/App with a
 * pre-filled message; the staff member presses Send themselves. Nothing here
 * sends anything, and there is no WhatsApp Business API involved.
 */

export type NormalizedPhone =
  /** Ready for `wa.me` — digits only, no `+`, no spaces, e.g. "919157249840". */
  | { ok: true; e164: string }
  | { ok: false; reason: 'missing' | 'malformed' };

/**
 * Turn whatever is stored on the document into a `wa.me`-shaped number.
 *
 * STRICT, BY PRODUCT DECISION. Every customer phone in this product is a
 * 10-digit Indian mobile under `+91` — there are no international customers
 * and none are planned (staff `User.phone` is a separate, unpopulated field
 * with no UI, and is out of scope here). Given that guarantee, this function
 * does not try to be clever about what counts as "probably a phone number" —
 * it normalises exactly one shape and REQUIRES the result to be exactly ten
 * digits. Anything else is `malformed`, full stop, which the caller renders as
 * a disabled button naming the expected shape: graceful degradation, never a
 * guessed `wa.me` link that silently goes nowhere.
 *
 * There is deliberately NO mobile-vs-landline disambiguation (an earlier draft
 * of this function had one, and a caveat about the case it could not catch).
 * That whole concern is RETIRED, not "accepted as a limitation" — the domain
 * guarantees a mobile, so a ten-digit result is treated as one.
 *
 * THE FOUR STEPS, IN ORDER:
 *   1. strip everything that is not a digit.
 *   2. drop ONE leading "00" (ISD dialling prefix) or a single leading "0"
 *      (STD trunk prefix) — whichever is present.
 *   3. if what is left starts with "91" AND removing it leaves EXACTLY ten
 *      digits, remove it — this is what stops an already-prefixed number
 *      ("919157249840") picking up a second "91", while NOT firing on a bare
 *      ten-digit number that happens to start with "91" itself
 *      ("9157249840" — stripping would leave only eight digits, so the guard
 *      refuses).
 *   4. require EXACTLY ten digits at this point. Ten -> `91` + those ten.
 *      Anything else -> `malformed`.
 *
 * Accepts `null`/`undefined` even where the caller's prop type already forbids
 * them. That is defence in depth for one line of cost: the values reaching here
 * come off DTOs whose optionality has changed before and will again, and the
 * honest answer for an absent number is `missing`, not a thrown TypeError inside
 * a click handler.
 */
export function normalizeWhatsAppPhone(raw: string | null | undefined): NormalizedPhone {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length === 0) return { ok: false, reason: 'missing' };

  let d = digits;

  if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = d.slice(1);

  if (d.startsWith('91') && d.length - 2 === 10) d = d.slice(2);

  if (d.length === 10) return { ok: true, e164: `91${d}` };

  return { ok: false, reason: 'malformed' };
}

/**
 * The deep link itself.
 *
 * `wa.me` rather than `api.whatsapp.com`: it is the documented short form, it
 * resolves to the installed app on mobile and to WhatsApp Web on desktop, and it
 * needs no `phone=` query parameter that could be dropped by a link rewriter.
 *
 * `encodeURIComponent` on the WHOLE message, not on parts of it. The message
 * contains a full `https://` URL whose own `?`/`&`/`=` must survive as literal
 * text inside `?text=` — encoding the assembled string is what makes that true,
 * and hand-assembling the query would silently truncate the share URL at its
 * first `&`.
 */
export function buildWhatsAppShareUrl(phoneE164: string, message: string): string {
  return `https://wa.me/${phoneE164}?text=${encodeURIComponent(message)}`;
}
